import { adjustDateRange } from '@frontend/common';
import { useEffect, useRef, useState } from 'react';

import { FromToDateInputProps } from './from-to-date-input.component';
import { clampDatePairToMaxRangeMs, getDateInputBounds } from './from-to-date-input.utils';

export const useFromToDateInput = (props: FromToDateInputProps) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const isFirstRender = useRef(true);
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [fromDate, setFromDate] = useState<string | null>(null);
    const [toDate, setToDate] = useState<string | null>(null);

    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });

    const portalRoot = document.getElementById('app-content');

    useEffect(() => {
        if (showMenu) {
            const triggerElement = document.getElementById('from-to-date-input');
            if (triggerElement) {
                const rect = triggerElement.getBoundingClientRect();
                setMenuPosition({
                    top: rect.bottom + 8,
                    left: rect.left,
                    width: rect.width
                });
            }
        }
    }, [showMenu]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const fromToDateInput = document.getElementById('from-to-date-input');
            if (fromToDateInput && fromToDateInput.contains(target)) {
                return;
            }
            if (menuRef.current && menuRef.current.contains(target)) {
                return;
            }
            setShowMenu(false);
        };

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMenu]);

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>, dateType: 'from' | 'to') => {
        const newDateStr = e.target.value;

        if (!fromDate && !toDate) {
            if (dateType === 'from') {
                setFromDate(new Date(newDateStr).toISOString());
            } else {
                setToDate(new Date(newDateStr).toISOString());
            }
            return;
        }

        const adjusted = adjustDateRange(fromDate, toDate, newDateStr, dateType);
        const clamped =
            props.maxRangeMs != null
                ? clampDatePairToMaxRangeMs(adjusted.fromDate, adjusted.toDate, props.maxRangeMs)
                : { fromDate: adjusted.fromDate, toDate: adjusted.toDate };
        setFromDate(clamped.fromDate.toISOString());
        setToDate(clamped.toDate.toISOString());
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        props.onChange(fromDate, toDate);
    }, [fromDate, toDate]);

    const dateInputBounds = getDateInputBounds(fromDate, toDate, props.maxRangeMs);

    return {
        menuRef,
        showMenu,
        setShowMenu,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        menuPosition,
        portalRoot,
        handleDateChange,
        dateInputBounds
    };
};
